// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
// eslint-disable-next-line spire/fenced-imports
import { hideAll, Instance as TippyInstance } from "tippy.js";

import FancyScroll from "@ces/App/Components/Common/FancyScroll";
import HeaderClickable from "@ces/App/Components/Header/HeaderClickable";
import { TippyPopupStyle } from "@ces/App/Components/Tippy/Style";
import TippyPopup from "@ces/App/Components/Tippy/TippyPopup";
import xHeaderUpdate from "@ces/App/Handlers/XNG/Header";
import { CustomAppState } from "@ces/App/Store/Reducers";
import AutocompleteProducts, { AutocompleteProductsStyles } from "@ces/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@ces/Overrides/Widgets/Header/SearchHistory";
import Close from "@ces/XDL/Icons/Close";
import Search from "@ces/XDL/Icons/Search";
import { mergeWithCss, parseBoolean } from "@ces/XDL/Utilities";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
// import AutocompleteProducts, {
//     AutocompleteProductsStyles,
// } from "@insite/content-library/Widgets/Header/AutocompleteProducts";
// import SearchHistory, { SearchHistoryStyles } from "@insite/content-library/Widgets/Header/SearchHistory";

import { xIsStockedItemsOnly } from "@ces/App/Store/Selectors/GeneralSelectors";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@ces/Overrides/Widgets/Header/AutocompleteCategories";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import AutocompleteSuggestions, {
    AutocompleteSuggestionsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import produce from "immer";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);

    // xng-changes
    const location = getLocation(state);
    const { stockedItemsOnly } = parseQueryString<{ stockedItemsOnly?: string }>(location.search);

    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location,
        searchPath,
        // XNg-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
        isMobileScreen: state.xCommon.isMobileScreen,
        showMobileInput: state.xHeader.showSearchSectionOnMobile,
        stockedItemsOnly:
            xIsStockedItemsOnly(state) && (stockedItemsOnly === undefined || parseBoolean(stockedItemsOnly)),
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    // XNG-Change
    xHeaderUpdate,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    // autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
    clickedProduct: boolean;

    // XNG-Changes
    autoCompleteModel?: AutocompleteModel & { productsContext: ProductContextModel[] };
    tippyInstance?: TippyInstance;
    isAutocompleteOpen?: boolean;
    stockedItemsOnly: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNG-Change
    tippyPopup?: TippyPopupStyle;
    blockWrapper?: InjectableCss;
    mobileInputWrapper?: InjectableCss;
    searchPopUpWrapper?: InjectableCss;
    tabletInputWrapper?: InjectableCss;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search },
        border: "underline",
        cssOverrides: {
            formField: css`
                width: 250px;
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: column-reverse;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 375px;
            padding: 0 20px 15px 20px;
        `,
    },
    autocompleteCommerceSearchWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 20px 15px 20px;
        `,
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;
    // XNG-Change: ref for fancy scroll
    contentRef = React.createRef<HTMLDivElement>();
    productContentRef = React.createRef<HTMLDivElement>();

    constructor(props: Props) {
        super(props);
        // XNG-Change
        this.styles = mergeWithCss(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            clickedProduct: true,
            // xng-change
            stockedItemsOnly: this.props.stockedItemsOnly,
        };
    }

    // XNG-Change
    inputWrapperRef = React.createRef<HTMLInputElement>();

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });
                // <XNG-Changes>
                // OOTB issue, when a user do search and return back to home page, the history not showAutocomplete update so the history popup not appear
                this.showAutocomplete = true;
                // </XNG-Changes>
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        } else {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(
                `/${this.props.searchPath}?query=${encodedQuery}&stockedItemsOnly=${this.state.stockedItemsOnly}`,
                false,
            );

            if (this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // if we already on the Search page then scroll to top
                window.scrollTo(0, 0);
            }
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }
    };

    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (this.showAutocomplete) {
                    this.setState({
                        // XNG-Change: add productsContext
                        autoCompleteModel: {
                            ...result,
                            productsContext: (result?.products || []).map(product => ({
                                product: {
                                    id: product.id!,
                                    productNumber: product?.displayErpNumber as any,
                                    mediumImagePath: product.image,
                                    productTitle: product.displayTitle as any,
                                    canonicalUrl: product.url,
                                    uri: product.uri,
                                    canAddToCart: parseBoolean(product.properties.canAddToCart),
                                    manufacturerItem: product.manufacturerItemNumber,
                                    minimumOrderQty: parseInt(product.properties.minimumOrderQty || "1", 10) || 1,
                                    brand: {
                                        name: product.brandName,
                                        detailPagePath: product.brandDetailPagePath,
                                    },
                                    customerProductNumber: product.isNameCustomerOverride ? product.name : "",
                                    properties: {
                                        ...product.properties,
                                        displayName: product.name || "",
                                    } as any,
                                    detail: {
                                        multipleSaleQty: parseInt(product.properties.multipleSaleQty || "1", 10) || 1,
                                    },
                                    unitOfMeasures: JSON.parse(product.properties.productUnitOfMeasures || "[]").map(
                                        (uom: any) => ({
                                            ...uom,
                                            id: uom.productUnitOfMeasureId,
                                        }),
                                    ),
                                } as ProductModel,
                                productInfo: {
                                    productId: product.id!,
                                    productDetailPath: product.url,
                                    qtyOrdered: parseInt(product.properties.minimumOrderQty || "1", 10) || 1,
                                    unitOfMeasure:
                                        JSON.parse(product.properties.productUnitOfMeasures || "[]").filter(
                                            (uom: any) => !uom.isDefault,
                                        )?.[0]?.unitOfMeasure || "",
                                    selectedUOMLineId: `Auto-Complete-${product.id}`,
                                },
                                onQtyOrderedChanged: (qty: number) => {
                                    const autoCompleteModel = produce(this.state.autoCompleteModel, draftedState => {
                                        if (!draftedState) {
                                            return;
                                        }
                                        const productIndex = draftedState.productsContext.findIndex(
                                            p => p.product.id === product.id,
                                        );
                                        draftedState.productsContext[productIndex].productInfo.qtyOrdered = qty;
                                    });
                                    this.setState({ autoCompleteModel });
                                },
                                onUnitOfMeasureChanged: unitOfMeasure => {
                                    const autoCompleteModel = produce(this.state.autoCompleteModel, draftedState => {
                                        if (!draftedState) {
                                            return;
                                        }
                                        const productContext = draftedState.productsContext.find(
                                            p => p.product.id === product.id,
                                        );

                                        if (!productContext) {
                                            return;
                                        }

                                        const uom = productContext.product.unitOfMeasures?.find(
                                            u => u.unitOfMeasure === unitOfMeasure,
                                        );

                                        productContext.productInfo.unitOfMeasure = unitOfMeasure;

                                        if (uom?.qtyPerBaseUnitOfMeasure) {
                                            productContext.productInfo.qtyOrdered = uom.qtyPerBaseUnitOfMeasure;
                                        }
                                    });
                                    this.setState({ autoCompleteModel });
                                },
                            })),
                        },
                    });
                    // XNG-change
                    this.updatePopoverVisibility(result);
                } else {
                    // XNG-change
                    this.updatePopoverVisibility();
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);
        // XNG-Changes
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    openAutocomplete = () => {
        // this.popover.current && (this.popover.current! as any).openPopover();
        // XNG-Changes
        this.state.tippyInstance?.show();
        this.setState({ isAutocompleteOpen: true });
    };

    closeAutocomplete = () => {
        // this.popover.current && (this.popover.current! as any).closePopover();
        // XNG-Changes
        this.state.tippyInstance?.hide();
        this.setState({ isAutocompleteOpen: false });
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }

        this.setState({
            clickedProduct: false,
        });

        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        const data = {
            searchQuery: this.state.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };

        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }

        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }
        // XNG-Change: use productsContext
        const {
            categories,
            brands,
            content,
            products,
            productsContext,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;

        // XNG-Change
        const inputWidth = this.inputWrapperRef.current?.getBoundingClientRect().width;
        const hasContent = !!categories?.length || !!brands?.length || !!content?.length || !!products?.length;
        // XNG-Change: Make sure list width inherited from its parent + add fancy scroll
        return (
            <FancyScroll refElement={this.contentRef}>
                <>
                    {isRetailSearchCompletionResults &&
                        ((suggestions && suggestions.length > 0) ||
                            (categories && categories.length > 0) ||
                            (popularCategories && popularCategories.length > 0) ||
                            (brands && brands.length > 0) ||
                            (popularBrands && popularBrands.length > 0)) && (
                            <StyledWrapper
                                {...this.styles.autocompleteCommerceSearchWrapper}
                                className="autocomplete-commerce-search-wrapper"
                            >
                                {suggestions && suggestions.length > 0 && (
                                    <AutocompleteSuggestions
                                        suggestions={suggestions}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                    />
                                )}
                                {((categories && categories.length > 0) ||
                                    (popularCategories && popularCategories.length > 0)) && (
                                    <AutocompleteCategories
                                        categories={categories}
                                        popularCategories={popularCategories}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteCategoriesStyles}
                                        isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                    />
                                )}
                                {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                    <AutocompleteBrands
                                        brands={brands}
                                        popularBrands={popularBrands}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteBrandsStyles}
                                        isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                    />
                                )}
                            </StyledWrapper>
                        )}

                    {!isRetailSearchCompletionResults && (
                        <StyledWrapper
                            {...this.styles.autocompleteWrapper}
                            className="autocomplete-wrapper"
                            {...{
                                width: inputWidth || 300,
                            }}
                            ref={this.contentRef}
                        >
                            {((categories && categories.length > 0) ||
                                (brands && brands.length > 0) ||
                                (content && content.length > 0)) && (
                                <StyledWrapper
                                    {...this.styles.autocompleteColumnWrapper}
                                    className="autocomplete-column-wrapper"
                                    {...{ hasContent: !!products?.length }}
                                >
                                    <StyledWrapper {...this.styles.blockWrapper}>
                                        <AutocompleteCategories
                                            categories={categories}
                                            focusedItem={this.state.focusedAutocompleteItem}
                                            goToUrl={this.goToUrl}
                                            extendedStyles={this.styles.autocompleteCategoriesStyles}
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper {...this.styles.blockWrapper}>
                                        <AutocompleteBrands
                                            brands={brands}
                                            focusedItem={this.state.focusedAutocompleteItem}
                                            goToUrl={this.goToUrl}
                                            extendedStyles={this.styles.autocompleteBrandsStyles}
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper {...this.styles.blockWrapper}>
                                        <AutocompleteContent
                                            content={content}
                                            focusedItem={this.state.focusedAutocompleteItem}
                                            goToUrl={this.goToUrl}
                                            extendedStyles={this.styles.autocompleteContentStyles}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                            {products && products.length > 0 && (
                                <FancyScroll
                                    refElement={this.productContentRef}
                                    extendedStyles={{
                                        css: css`
                                            width: 100%;
                                        `,
                                    }}
                                >
                                    <StyledWrapper
                                        {...this.styles.autocompleteColumnWrapper}
                                        className="autocomplete-column-wrapper"
                                        ref={this.productContentRef}
                                        {...{ productsWrapper: true }}
                                    >
                                        <AutocompleteProducts
                                            products={productsContext}
                                            focusedItem={this.state.focusedAutocompleteItem}
                                            goToUrl={this.goToUrl}
                                            extendedStyles={this.styles.autocompleteProductsStyles}
                                        />
                                    </StyledWrapper>
                                </FancyScroll>
                            )}
                        </StyledWrapper>
                    )}
                </>
            </FancyScroll>
        );
    };

    // <XNG-Changes>
    isPopoverVisible(results: AutocompleteModel | undefined) {
        let isVisible: boolean | null = false;
        if (this.state.query && this.state.query.length > 2 && results) {
            const { categories, brands, content, products } = results;
            isVisible =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (products && products.length > 0));
        } else if (!this.state.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        }
        return isVisible;
    }
    // XNG-Change
    updatePopoverVisibility(results?: AutocompleteModel) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    handleInputFocus = () => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
        this.props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
        this.props.setFlyoutDrawerIsOpen({ isOpen: false });
    };

    // XNG-Change
    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility();
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;

        const searchInput = (
            <TextField
                {...styles.input}
                ref={this.props.inputRef}
                isAnimated={this.props.isAnimated}
                iconClickableProps={{ onClick: this.doSearch }}
                onSubmit={this.doSearch}
                value={this.state.query}
                onChange={ev => this.handleInputChange(ev, true)} // XNG-Change: Pass true
                placeholder={translate("Search by Keyword")}
                autoComplete="off"
                clickableText={translate("submit search")}
                id={searchId}
                data-test-selector="headerSearchInputTextField"
                className="search-input"
                // XNG-Change
                onKeyDown={this.handlePopoverKeyDown}
                aria-multiline="false"
                aria-required="false"
                aria-expanded={this.state.isAutocompleteOpen}
                onFocus={this.handleInputFocus}
                maxLength={100}
            />
        );

        // XNG-Change
        if (this.props.isMobileScreen && !this.props.showMobileInput) {
            return (
                <HeaderClickable
                    iconProps={{
                        src: Search,
                    }}
                    onClick={() => {
                        this.props.xHeaderUpdate({
                            key: "showSearchSectionOnMobile",
                            value: true,
                        });
                    }}
                />
            );
        }
        // XNG-change
        const inputWidth = this.inputWrapperRef.current?.getBoundingClientRect().width;
        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                {/* XNG-Changes: change popover with tippy */}
                <TippyPopup
                    onCreate={instance => {
                        this.setState({ tippyInstance: instance });
                    }}
                    onShow={instance => {
                        xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_search` });
                        this.setState({ tippyInstance: instance });
                    }}
                    onHidden={instance => {
                        this.setState({ isAutocompleteOpen: false });
                        xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_search` });
                    }}
                    content={
                        <>
                            {!this.state.query && this.showAutocomplete && (
                                // XNG-Change: add wrapper
                                <StyledWrapper {...styles.searchPopUpWrapper} {...{ width: inputWidth || 300 }}>
                                    <SearchHistory
                                        focusedQuery={this.state.focusedSearchHistoryQuery}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={styles.searchHistoryStyles}
                                        clearSearchHistoryCallback={this.clearSearchHistoryCallback}
                                    />
                                </StyledWrapper>
                            )}
                            {this.state.query.length >= 3 && this.renderAutocomplete()}
                        </>
                    }
                    trigger="manual"
                    offset={[0, 0]}
                    duration={[null, null]}
                    animation={false}
                    arrow={false}
                    maxWidth="none"
                    placement="bottom-start"
                    type="menu"
                    mobileMode="left-side"
                    showCloseButton={false}
                    appendTo={() => document.body}
                    extendedStyles={styles.tippyPopup}
                    popperOptions={{
                        strategy: this.props.isTabletScreen ? "fixed" : "absolute",
                        modifiers: [
                            {
                                name: "preventOverflow",
                                options: {
                                    padding: 0,
                                },
                            },
                            {
                                name: "computeStyles",
                                options: {
                                    adaptive: !this.props.isMobileScreen,
                                    roundOffsets: ({ x, y }: any) => ({
                                        x: this.props.isTabletScreen ? 0 : x,
                                        y: this.props.isTabletScreen ? 0 : y,
                                    }),
                                },
                            },
                        ],
                    }}
                >
                    {/* XNG-Change Add mobile style, and add X in mobile */}
                    <StyledWrapper ref={this.inputWrapperRef} {...styles.mobileInputWrapper}>
                        <StyledWrapper {...styles.tabletInputWrapper}>
                            {searchInput}
                            {!this.props.isMobileScreen && this.props.isTabletScreen && this.state.query && (
                                <HeaderClickable
                                    iconProps={{
                                        src: Close,
                                        size: 14,
                                    }}
                                    onClick={() => {
                                        this.setState({ query: "", isAutocompleteOpen: false });
                                        hideAll();
                                    }}
                                />
                            )}
                        </StyledWrapper>
                        {this.props.showMobileInput && (
                            <HeaderClickable
                                iconProps={{
                                    src: Close,
                                    size: 14,
                                }}
                                onClick={() => {
                                    this.props.xHeaderUpdate({
                                        key: "showSearchSectionOnMobile",
                                        value: false,
                                    });
                                }}
                            />
                        )}
                    </StyledWrapper>
                </TippyPopup>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
